import styled, { css, keyframes } from 'styled-components'

export const UpsellPricingToggleContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 1.5rem 0;
`

export const UpsellPricingToggle = styled.div`
  display: flex;
  border-radius: 50px;
  overflow: hidden;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  border: 1px solid ${({ theme }) => theme.palette.border.main};
`

export const UpsellPricingToggleButton = styled.button<{ active: boolean }>`
  padding: 0.75rem 1.5rem;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  border: none;
  outline: none;
  transition: all 0.3s ease;
  position: relative;

  ${({ active, theme }) =>
    active
      ? css`
          background-color: ${theme.palette.primary.main};
          color: white;
          font-weight: 600;
          transform: scale(1.02);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          z-index: 2;

          &::before {
            content: '';
            position: absolute;
            top: -2px;
            left: -2px;
            right: -2px;
            bottom: -2px;
            background: linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.main});
            border-radius: inherit;
            z-index: -1;
            opacity: 0.3;
          }
        `
      : css`
          background-color: ${theme.palette.background.default};
          color: ${theme.palette.text.primary};
          font-weight: 500;
          transform: scale(1);
          box-shadow: none;
          z-index: 1;
        `}
`

export const UpsellTaxNote = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.palette.text.secondary};
  margin-top: 0.5rem;
  text-align: center;
  opacity: 0.85;
  letter-spacing: 0.02em;
`

export const UpsellPricingGrid = styled.div<{ showOnlyAdFree?: boolean }>`
  display: grid;
  grid-template-columns: ${({ showOnlyAdFree }) => (showOnlyAdFree ? '1fr' : '1fr 1fr')};
  gap: 1.5rem;
  justify-items: ${({ showOnlyAdFree }) => (showOnlyAdFree ? 'center' : 'stretch')};
  max-width: ${({ showOnlyAdFree }) => (showOnlyAdFree ? '400px' : 'none')};
  margin: 0 auto;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`

export const UpsellPricingCard = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  border: 0.5px solid ${({ theme }) => theme.palette.border.main};
  transition: all 0.3s ease;
  background-color: ${({ theme }) => theme.palette.background.default};
  position: relative;
  overflow: hidden;
  padding: 1rem;
  width: 100%;
  max-width: 400px;

  &:hover {
    border-color: ${({ theme }) => theme.palette.primary.main};

    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`

export const UpsellSavingsBadge = styled.div`
  position: absolute;
  top: 0.5rem;
  right: -3.2rem;
  background-color: ${({ theme }) => theme.palette.success.main};
  color: white;
  padding: 0.35rem 0;
  font-size: 0.75rem;
  font-weight: 600;
  z-index: 1;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transform: rotate(45deg);
  text-align: center;
  width: 200px;
  letter-spacing: 0.5px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      45deg,
      rgba(255, 255, 255, 0.1) 0%,
      rgba(255, 255, 255, 0.2) 50%,
      rgba(255, 255, 255, 0.1) 100%
    );
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border: 1px solid rgba(255, 255, 255, 0.2);
    pointer-events: none;
  }

  animation: ${keyframes`
    0% {
      transform: rotate(45deg) scale(1);
    }
    50% {
      transform: rotate(45deg) scale(1.03);
    }
    100% {
      transform: rotate(45deg) scale(1);
    }
  `} 2s ease-in-out infinite;
`

export const UpsellPricingHeader = styled.div`
  margin-bottom: 1.5rem;
  text-align: center;
`

export const UpsellPricingTitle = styled.h3`
  margin: 0 0 1rem;
  font-size: 1.5rem;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.text.primary};
`

export const UpsellPricingPrice = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.palette.text.primary};
  display: flex;
  align-items: flex-end;
  justify-content: center;
`

export const UpsellPricingPeriod = styled.span`
  font-size: 1rem;
  font-weight: 400;
  margin-left: 0.25rem;
  color: ${({ theme }) => theme.palette.text.secondary};
`

export const UpsellTrialBadge = styled.div`
  background-color: ${({ theme }) => theme.palette.primary.light};
  color: #fff;
  padding: 0.5rem 1rem;
  border-radius: 1rem;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  display: inline-block;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
`

export const UpsellPricingFeatures = styled.div`
  margin-bottom: 1.5rem;
  flex-grow: 1;
  display: flex;
  padding: 0rem 2rem;
  flex-direction: column;
  gap: 0.5rem;
`

export const UpsellPricingFeature = styled.div`
  margin-bottom: 0.75rem;
  color: ${({ theme }) => theme.palette.text.secondary};
  font-size: 0.9rem;
  line-height: 1.4;
  text-align: left;
`

export const UpsellPricingFooter = styled.div`
  margin-top: auto;
`

export const UpsellModal = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: ${({ theme }) => theme.palette.background.paper};
  border-radius: 12px;
  padding: 2rem;
  max-width: 800px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  z-index: ${({ theme }) => theme.zIndex.upsellModal};
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
`

export const UpsellModalBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: ${({ theme }) => theme.zIndex.upsellModal - 1};
`

export const UpsellModalContent = styled.div`
  p {
    margin: 0 0 1.5rem 0;
    color: ${({ theme }) => theme.palette.text.secondary};
    line-height: 1.6;
  }
`

export const UpsellModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`

export const UpsellModalTitle = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.palette.text.primary};
  font-size: 1.5rem;
  font-weight: 600;
`

export const UpsellAskMeLaterButton = styled.button`
  background: transparent;
  color: ${({ theme }) => theme.palette.text.secondary};
  border: none;
  border-radius: 0;
  padding: 8px 0;
  font-size: 0.8125rem;
  font-weight: 400;
  cursor: pointer;
  margin-top: 12px;
  transition: color 0.2s ease;
  text-decoration: underline;
  text-decoration-color: transparent;
  opacity: 0.7;

  &:hover {
    color: ${({ theme }) => theme.palette.text.primary};
    text-decoration-color: ${({ theme }) => theme.palette.text.primary};
    opacity: 1;
  }
`

// Styled components for popup (similar to tm-coach-ad-popup)
export const UpsellPopupBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(5px);
  z-index: ${({ theme }) => theme.zIndex.upsellModal - 1};
  animation: fadeIn 0.3s ease-out forwards;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`

export const UpsellPopupContainer = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 800px;
  max-width: 90vw;
  max-height: 90vh;
  background: ${({ theme }) => theme.palette.background.paper};
  border-radius: 8px;
  box-shadow: ${({ theme }) => theme.palette.shadow.main};
  z-index: ${({ theme }) => theme.zIndex.upsellModal};
  overflow-y: auto;
  animation: zoomIn 0.2s ease-out forwards;

  @keyframes zoomIn {
    from {
      transform: translate(-50%, -50%) scale(1.3);
      opacity: 0;
    }
    to {
      transform: translate(-50%, -50%) scale(1);
      opacity: 1;
    }
  }
`

export const UpsellPopupHeader = styled.div`
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.palette.primary.main} 0%,
    ${({ theme }) => theme.palette.primary.light} 100%
  );
  padding: 20px;
  text-align: center;
  color: ${({ theme }) => theme.palette.primary.contrastText};
`

export const UpsellHeaderText = styled.div`
  font-size: 18px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  line-height: 1.2;
`

export const UpsellPopupContent = styled.div`
  padding: 20px;
  text-align: center;

  p {
    margin: 5px 0 15px;
    color: ${({ theme }) => theme.palette.text.primary};
  }
`

export const UpsellPopupButton = styled.button`
  background: ${({ theme }) => theme.palette.primary.main};
  color: ${({ theme }) => theme.palette.primary.contrastText};
  border: none;
  border-radius: 4px;
  padding: 10px 15px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  margin: 15px 0;
  width: 100%;
  transition: background 0.2s;

  &:hover {
    background: ${({ theme }) => theme.palette.primary.light};
  }
`

// Weakness Display Styled Components
export const UpsellWeaknessSection = styled.div`
  background-color: ${({ theme }) => theme.palette.background.paper};
  border-radius: 8px;
  padding: 1.25rem;
  margin: 1rem 0;
  border: 1px solid ${({ theme }) => theme.palette.border.main};
  position: relative;

  &::before {
    content: '⭐ AI Can Fix These';
    position: absolute;
    top: -10px;
    right: 1rem;
    background: ${({ theme }) => theme.palette.primary.main};
    color: white;
    padding: 0.25rem 0.75rem;
    border-radius: 12px;
    font-size: 0.75rem;
    font-weight: 600;
  }
`

export const UpsellWpmDisplay = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.palette.border.main};

  .wpm-info {
    display: flex;
    flex-direction: column;
  }

  .wpm-label {
    font-size: 0.75rem;
    font-weight: 500;
    color: ${({ theme }) => theme.palette.text.secondary};
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 0.25rem;
  }

  .wpm-display {
    display: flex;
    align-items: baseline;
    gap: 0.25rem;
  }

  .wpm-value {
    font-size: 1.75rem;
    font-weight: 700;
    color: ${({ theme }) => (theme.palette.mode === 'dark' ? theme.palette.text.primary : theme.palette.primary.main)};
    line-height: 1;
  }

  .wpm-unit {
    font-size: 0.875rem;
    font-weight: 500;
    color: ${({ theme }) => theme.palette.text.secondary};
  }
`

export const UpsellWeaknessTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.text.primary};
  margin: 0;
  text-align: right;
  align-self: center;
`

export const UpsellWeaknessCategory = styled.div`
  margin-bottom: 1rem;

  &:last-child {
    margin-bottom: 0;
  }
`

export const UpsellWeaknessCategoryLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.5rem;
  text-align: left;
`

export const UpsellWeaknessItems = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  justify-content: flex-start;
`

export const UpsellWeaknessChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.375rem 0.75rem;
  background-color: ${({ theme }) => theme.palette.error.main}10;
  color: ${({ theme }) => theme.palette.error.main};
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 500;
  font-family: monospace;
  border: 1px solid ${({ theme }) => theme.palette.error.main}30;
`

export const UpsellPrimaryCTA = styled.button`
  background: ${({ theme }) => theme.palette.primary.main};
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.75rem 1.25rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  margin: 1.5rem auto 1rem;
  width: auto;
  display: block;
  transition: all 0.2s ease;
  box-shadow: 0 2px 6px ${({ theme }) => theme.palette.primary.main}30;

  &:hover {
    background: ${({ theme }) => theme.palette.primary.light};
    box-shadow: 0 3px 8px ${({ theme }) => theme.palette.primary.main}40;
  }

  &:active {
    transform: scale(0.98);
  }
`

export const UpsellTrialNote = styled.div`
  text-align: center;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.palette.text.secondary};
  margin: 0.5rem auto 1rem;
  font-weight: 400;
  line-height: 1.4;
`

export const UpsellEmptyStateText = styled.p`
  color: ${({ theme }) => theme.palette.text.secondary};
  font-size: 1rem;
  text-align: center;
  margin: 2rem 0;
  line-height: 1.6;
`
